// stickerRecent — 메신저 스티커 창 «최근» 탭의 저장소(브라우저 localStorage · 사용자별).
//
// 설계: `patch_mm_sticker_recent_tab.md` §2-1. 서버에 저장하지 않는다(마스터 결정: «최근 탭 하나로
// 브라우저 저장으로 진행해») — 그래서 PC·브라우저를 옮기면 최근 목록은 따라가지 않는다.
//
// 🔴 키는 **사용자별**이다 — 같은 PC 를 둘이 쓸 때 남의 최근 목록이 보이면 안 된다.
// 🔴 사용자 id 를 모르면(로그인 전·목) **읽지도 쓰지도 않는다.** 'anon' 같은 공유 키를 두면
//    로그인 전 흔적이 다음 사람에게 보이고, 시험에서도 한 시험이 남긴 기록이 다음 시험의
//    기본 탭을 바꿔 놓는다.
// 🔴 버전(v)은 저장하지 않는다 — 돌려주는 item 은 항상 **지금 manifest 의 객체**다. v 를 저장해
//    두면 이미지를 교체한 뒤에도 옛 판을 보내게 된다.
// 🔴 localStorage 접근은 전부 함수 안 try/catch 다(사파리 프라이빗 등은 getItem 도 던진다).
//    모듈 최상위에서는 저장소를 건드리지 않는다.
// 마지막 탭(10-30 마스터 지시 · `patch_mm_sticker_remember_tab.md` §2-1) — 사용자가 마지막에 «누른» 탭
//    키(':recent' 또는 팩 이름)를 같은 규칙(사용자별 키 · id 모르면 안 씀 · try/catch)으로 평문 저장한다.
import { getGatewayUserId } from '../lib/ncSso';

export const RECENT_MAX = 16;
export const RECENT_KEY_PREFIX = 'gw_sticker_recent_v1:';
export const TAB_KEY_PREFIX = 'gw_sticker_tab_v1:';
// «최근» 탭의 키. 🔴 팩 이름 규칙에 ':' 가 없어 어떤 팩 이름과도 충돌하지 않는다(StickerPicker 가 이 값을 쓴다).
export const RECENT_TAB_KEY = ':recent';
// 팩 이름 규칙 — backend/routers/stickers.py 와 같다.
const PACK_NAME_RE = /^[a-z0-9_-]{1,64}$/;

// 사용자 id 를 모르면 빈 문자열 = «저장소를 쓰지 않는다».
function storageKey(prefix) {
  try {
    const uid = getGatewayUserId();
    return uid ? prefix + String(uid) : '';
  } catch (e) {
    return '';
  }
}

// 탭 키는 ':recent' 또는 팩 이름 규칙에 맞을 때만 유효하다 — 그 밖은 빈 문자열.
function normTab(value) {
  const key = value === undefined || value === null ? '' : String(value);
  return (key === RECENT_TAB_KEY || PACK_NAME_RE.test(key)) ? key : '';
}

// 저장 항목은 `{pack, id}` 둘 다 빈 값이 아닌 문자열일 때만 유효하다.
function normEntry(row) {
  if (!row || typeof row !== 'object') return null;
  const pack = row.pack === undefined || row.pack === null ? '' : String(row.pack);
  const id = row.id === undefined || row.id === null ? '' : String(row.id);
  if (!pack || !id) return null;
  return { pack, id };
}

/** 최근 목록(최신이 맨 앞). 저장값이 깨졌으면 그 원소만(또는 전체를) 버리고 예외는 내지 않는다. */
export function readRecent() {
  const key = storageKey(RECENT_KEY_PREFIX);
  if (!key) return [];
  let raw = null;
  try {
    raw = localStorage.getItem(key);
  } catch (e) {
    return [];
  }
  if (!raw) return [];
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out = [];
  for (let i = 0; i < parsed.length && out.length < RECENT_MAX; i += 1) {
    const entry = normEntry(parsed[i]);
    if (entry) out.push(entry);
  }
  return out;
}

/** 보낸 스티커를 맨 앞에 기록한다(중복은 앞으로 당기고, RECENT_MAX 로 자른다). */
export function pushRecent(pack, id) {
  const key = storageKey(RECENT_KEY_PREFIX);
  if (!key) return;
  const entry = normEntry({ pack, id });
  if (!entry) return;
  const rest = readRecent().filter((row) => !(row.pack === entry.pack && row.id === entry.id));
  const next = [entry].concat(rest).slice(0, RECENT_MAX);
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch (e) {
    /* 전송은 이미 끝났다 — 기록 실패를 사용자 오류로 보이지 않는다 */
  }
}

/** 마지막에 누른 탭 키(':recent' 또는 팩 이름). 없거나 규칙 밖이면 ''. 지금 manifest 에 있는지는 호출부가 본다. */
export function readLastTab() {
  const key = storageKey(TAB_KEY_PREFIX);
  if (!key) return '';
  let raw = null;
  try {
    raw = localStorage.getItem(key);
  } catch (e) {
    return '';
  }
  return normTab(raw);
}

/** 사용자가 **누른** 탭만 기록한다(기본 탭을 정하는 쪽에서는 부르지 않는다). 규칙 밖 값은 쓰지 않는다. */
export function writeLastTab(tab) {
  const key = storageKey(TAB_KEY_PREFIX);
  if (!key) return;
  const value = normTab(tab);
  if (!value) return;
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    /* 탭 전환은 이미 됐다 — 기억 실패를 사용자 오류로 보이지 않는다 */
  }
}

/**
 * 저장 항목을 지금 manifest 로 해석한다 → `[{pack: <팩 객체>, item: <스티커 객체>}]`(순서 유지).
 * manifest 에서 빠진 팩·스티커는 버린다(저장값은 고치지 않는다 — 다음 pushRecent 때 밀려난다).
 */
export function resolveRecent(entries, packs) {
  const rows = Array.isArray(entries) ? entries : [];
  const list = Array.isArray(packs) ? packs : [];
  // 🔴 Map 으로 담는다 — 팩 이름 규칙(^[a-z0-9_-]{1,64}$)은 'constructor' 도 통과해서
  //    평범한 객체에 담으면 Object.prototype 의 값이 «있다» 로 보인다.
  const byPack = new Map();
  list.forEach((p) => {
    if (!p || p.pack === undefined || p.pack === null) return;
    const name = String(p.pack);
    if (!name || byPack.has(name)) return;
    const items = new Map();
    const stickers = Array.isArray(p.stickers) ? p.stickers : [];
    stickers.forEach((item) => {
      if (!item || item.id === undefined || item.id === null) return;
      const id = String(item.id);
      if (!id || items.has(id)) return;
      items.set(id, item);
    });
    byPack.set(name, { pack: p, items });
  });
  const out = [];
  rows.forEach((row) => {
    const entry = normEntry(row);
    if (!entry) return;
    const hit = byPack.get(entry.pack);
    if (!hit) return;
    const item = hit.items.get(entry.id);
    if (!item) return;
    out.push({ pack: hit.pack, item });
  });
  return out;
}
